import { inject, Injectable, InjectionToken } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { catchError, EMPTY, filter, map, switchMap, take } from 'rxjs';
import { environment } from '@environments/environment';
import Intercom from '@intercom/messenger-js-sdk';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { intercomID } from '@shared/constants';
import { intercomMessengerTokenQuery } from '@services/queries/queries/intercom.queries';

/** The SDK's boot call, injectable so specs don't load the real widget. */
export const INTERCOM_BOOT = new InjectionToken<typeof Intercom>(
  'INTERCOM_BOOT',
  { providedIn: 'root', factory: () => Intercom }
);

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private _oidcSecurityService = inject(OidcSecurityService);
  private _apollo = inject(Apollo);
  private _boot = inject(INTERCOM_BOOT);

  /**
   * The API signs the user's identity, so the Messenger secret never reaches
   * the browser. Without a token (Intercom not set up) the Messenger stays off.
   */
  initIntercom() {
    this._oidcSecurityService.userData$
      .pipe(
        filter(({ userData }) => userData),
        take(1),
        switchMap(({ userData }) =>
          this._apollo
            .query<{ intercomMessengerToken: string | null }>({
              query: intercomMessengerTokenQuery,
              fetchPolicy: 'no-cache',
            })
            .pipe(
              map(({ data }) => ({
                userData,
                token: data?.intercomMessengerToken,
              })),
              // No token, no Messenger: a failed request just leaves it off.
              catchError(() => EMPTY)
            )
        ),
        filter(({ token }) => !!token)
      )
      .subscribe(({ userData: { name, created_timestamp }, token }) => {
        this._boot({
          app_id: environment.intercom.appID,
          intercom_user_jwt: token,
          name: name,
          created_at: Math.trunc(created_timestamp / 1000),
          custom_launcher_selector: `#${intercomID}`,
          hide_default_launcher: true,
        });
      });
  }
}
